import { useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod/v3";
import { formatDistanceToNow } from "date-fns";
import {
  Megaphone,
  Wrench,
  Sparkles,
  MessageSquare,
  Send,
  Users,
  Building2,
  Radio,
} from "lucide-react";
import { usePageTitle } from "@/hooks/use-page-title";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import {
  adminNotificationsQueryOptions,
  useAdminSendNotification,
} from "@/lib/api/admin";

export const Route = createFileRoute("/dashboard/admin/notifications")({
  loader: ({ context: { queryClient } }) =>
    queryClient.ensureQueryData(adminNotificationsQueryOptions({})),
  component: AdminNotificationsPage,
});

const notificationSchema = z.object({
  scope: z.enum(["user", "tenant", "broadcast"]),
  targetId: z.string().optional(),
  type: z.enum([
    "admin_announcement",
    "admin_maintenance",
    "admin_feature_update",
    "admin_custom",
  ]),
  title: z.string().min(1, "Title is required").max(200),
  message: z.string().max(1000).optional(),
});

type NotificationFormValues = z.infer<typeof notificationSchema>;

const typeOptions = [
  { value: "admin_announcement", label: "Announcement", icon: Megaphone },
  { value: "admin_maintenance", label: "Maintenance", icon: Wrench },
  { value: "admin_feature_update", label: "Feature Update", icon: Sparkles },
  { value: "admin_custom", label: "Custom", icon: MessageSquare },
] as const;

const scopeOptions = [
  {
    value: "broadcast",
    label: "Broadcast",
    description: "All users",
    icon: Radio,
  },
  {
    value: "user",
    label: "User",
    description: "Specific user",
    icon: Users,
  },
  {
    value: "tenant",
    label: "Team",
    description: "Specific team",
    icon: Building2,
  },
] as const;

const scopeBadgeVariants: Record<string, string> = {
  broadcast: "bg-rose-500/10 text-rose-500",
  user: "bg-blue-500/10 text-blue-500",
  tenant: "bg-amber-500/10 text-amber-500",
  guild: "bg-emerald-500/10 text-emerald-500",
};

function AdminNotificationsPage() {
  usePageTitle("Admin - Notifications");
  const [historyPage, setHistoryPage] = useState(0);
  const pageSize = 25;

  const form = useForm<NotificationFormValues>({
    resolver: zodResolver(notificationSchema),
    defaultValues: {
      scope: "broadcast",
      type: "admin_announcement",
      title: "",
      message: "",
    },
  });

  const sendNotification = useAdminSendNotification();
  const scope = form.watch("scope");

  const historyQuery = useQuery(
    adminNotificationsQueryOptions({
      offset: historyPage * pageSize,
      limit: pageSize,
    })
  );

  function onSubmit(values: NotificationFormValues) {
    sendNotification.mutate(values, {
      onSuccess: () => {
        form.reset();
      },
    });
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <div className="flex size-8 items-center justify-center rounded-lg bg-gradient-to-br from-rose-500/10 to-red-500/10">
              <Send className="size-4 text-rose-500" />
            </div>
            Send Notification
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="scope"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="flex items-center gap-2">
                        <Radio className="size-4 text-rose-500" />
                        Scope
                      </FormLabel>
                      <Select
                        onValueChange={field.onChange}
                        defaultValue={field.value}
                      >
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {scopeOptions.map((opt) => (
                            <SelectItem key={opt.value} value={opt.value}>
                              <div className="flex items-center gap-2">
                                <opt.icon className="size-3.5" />
                                {opt.label}
                                <span className="text-muted-foreground text-xs">
                                  ({opt.description})
                                </span>
                              </div>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="type"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="flex items-center gap-2">
                        <Megaphone className="size-4 text-rose-500" />
                        Type
                      </FormLabel>
                      <Select
                        onValueChange={field.onChange}
                        defaultValue={field.value}
                      >
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {typeOptions.map((opt) => (
                            <SelectItem key={opt.value} value={opt.value}>
                              <div className="flex items-center gap-2">
                                <opt.icon className="size-3.5" />
                                {opt.label}
                              </div>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              {scope !== "broadcast" && (
                <FormField
                  control={form.control}
                  name="targetId"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="flex items-center gap-2">
                        {scope === "user" ? (
                          <Users className="size-4 text-rose-500" />
                        ) : (
                          <Building2 className="size-4 text-rose-500" />
                        )}
                        {scope === "user" ? "User ID" : "Team ID"}
                      </FormLabel>
                      <FormControl>
                        <Input
                          placeholder={
                            scope === "user"
                              ? "Enter user ID..."
                              : "Enter team ID..."
                          }
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              )}

              <FormField
                control={form.control}
                name="title"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Title</FormLabel>
                    <FormControl>
                      <Input placeholder="Notification title..." {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="message"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Message (optional)</FormLabel>
                    <FormControl>
                      <Textarea
                        placeholder="Optional message body..."
                        rows={3}
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <Button
                type="submit"
                disabled={sendNotification.isPending}
                className="gap-2 bg-gradient-to-r from-rose-500 to-red-500 text-white hover:opacity-90"
              >
                <Send className="size-4" />
                {sendNotification.isPending
                  ? "Sending..."
                  : "Send Notification"}
              </Button>
            </form>
          </Form>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <div className="flex size-8 items-center justify-center rounded-lg bg-gradient-to-br from-rose-500/10 to-red-500/10">
              <Megaphone className="size-4 text-rose-500" />
            </div>
            Notification History
          </CardTitle>
        </CardHeader>
        <CardContent>
          {historyQuery.isLoading ? (
            <div className="text-muted-foreground py-8 text-center text-sm">
              Loading...
            </div>
          ) : !historyQuery.data?.data.length ? (
            <div className="text-muted-foreground py-8 text-center text-sm">
              No notifications sent yet
            </div>
          ) : (
            <div className="space-y-2">
              {historyQuery.data.data.map((n) => (
                <div
                  key={n.id}
                  className="flex items-start gap-3 rounded-lg border p-3"
                >
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="truncate text-sm font-medium">
                        {n.title}
                      </span>
                      <Badge
                        variant="secondary"
                        className={scopeBadgeVariants[n.scope] ?? ""}
                      >
                        {n.scope}
                      </Badge>
                      <Badge variant="outline" className="text-xs">
                        {n.type.replace("admin_", "").replace("_", " ")}
                      </Badge>
                    </div>
                    {n.message && (
                      <p className="text-muted-foreground mt-1 text-xs">
                        {n.message}
                      </p>
                    )}
                    <p className="text-muted-foreground/60 mt-1 text-xs">
                      {formatDistanceToNow(new Date(n.createdAt), {
                        addSuffix: true,
                      })}
                    </p>
                  </div>
                </div>
              ))}

              <div className="flex items-center justify-between pt-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={historyPage === 0}
                  onClick={() => setHistoryPage((p) => Math.max(0, p - 1))}
                >
                  Previous
                </Button>
                <span className="text-muted-foreground text-xs">
                  Page {historyPage + 1}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={
                    (historyQuery.data?.total ?? 0) <=
                    (historyPage + 1) * pageSize
                  }
                  onClick={() => setHistoryPage((p) => p + 1)}
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
